/**
 * Shared idle-aware time segments for project, lead, and CRM-wide tracking.
 *
 * Dual measures (when dualMeasures=true):
 * - activeSeconds: tab visible + strict input within 30s (no mousemove)
 * - presenceSeconds: tab visible + focused window + any input within 180s
 *   (mousemove counts, throttled)
 *
 * Short segments are not dropped below 1 second; callers may carry remainders.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const IDLE_THRESHOLD_MS = 30 * 1000;
/** Presence / reading allowance (mousemove counts). */
export const PRESENCE_IDLE_THRESHOLD_MS = 180 * 1000;
/** @deprecated Prefer PRESENCE_IDLE_THRESHOLD_MS — kept for POC callers. */
export const POC_IDLE_THRESHOLD_MS = 120 * 1000;
export const MIN_SAVE_MINUTES = 1 / 60; // 1 second floor
export const DEFAULT_FLUSH_INTERVAL_MS = 60 * 1000;
/** Throttle mousemove/pointermove activity bumps. */
export const MOVE_ACTIVITY_THROTTLE_MS = 1_000;

export const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'pointerdown',
  'keydown',
  'touchstart',
  'wheel',
  'scroll',
];

/** Extra input events for presence (mousemove + focus + keyup). */
export const PRESENCE_EXTRA_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'pointermove',
  'keyup',
  'focus',
];

/** @deprecated Alias for PRESENCE_EXTRA_ACTIVITY_EVENTS. */
export const POC_EXTRA_ACTIVITY_EVENTS = PRESENCE_EXTRA_ACTIVITY_EVENTS;

export function shouldAccumulateTime(
  lastActivityAt: number,
  now: number = Date.now(),
  idleThresholdMs: number = IDLE_THRESHOLD_MS,
): boolean {
  if (typeof document !== 'undefined' && document.hidden) return false;
  return now - lastActivityAt <= idleThresholdMs;
}

export function isWindowFocused(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.hidden) return false;
  if (typeof document.hasFocus === 'function') return document.hasFocus();
  return true;
}

export interface IdleTimeSegment {
  start: Date;
  end: Date;
  minutes: number;
  /** Strict active seconds within the segment (input ≤30s, no mousemove-only). */
  activeSeconds: number;
  /** Presence seconds within the segment (visible+focused, input ≤180s). */
  presenceSeconds: number;
  /** Value of `sessionKey` when the segment started (survives key changes). */
  sessionKey: string | null;
}

export type IdleSegmentHandler = (segment: IdleTimeSegment) => void | Promise<void>;

interface UseIdleAwareTimeSegmentsOptions {
  enabled: boolean;
  /** When this changes, the open segment is flushed under the previous key. */
  sessionKey?: string | null;
  flushIntervalMs?: number;
  tickIntervalMs?: number;
  trackElapsed?: boolean;
  minSaveMinutes?: number;
  onSegment: IdleSegmentHandler;
  /** Override idle cutoff for the open-segment clock (default IDLE or PRESENCE). */
  idleThresholdMs?: number;
  /** Extra window events treated as activity (e.g. mousemove, focus). */
  extraActivityEvents?: readonly (keyof WindowEventMap)[];
  /**
   * When idle is detected, close the segment at lastActivity + threshold
   * instead of Date.now() (avoids trailing inflation).
   */
  closeAtLastActivityPlusThreshold?: boolean;
  /** Throttle interval for mousemove/pointermove (0 = no throttle). */
  moveThrottleMs?: number;
  /**
   * Track both activeSeconds (30s strict) and presenceSeconds (180s + move).
   * When true, the open-segment clock uses presence rules.
   */
  dualMeasures?: boolean;
}

export function useIdleAwareTimeSegments({
  enabled,
  sessionKey = null,
  flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS,
  tickIntervalMs = 1000,
  trackElapsed = false,
  minSaveMinutes = MIN_SAVE_MINUTES,
  onSegment,
  idleThresholdMs,
  extraActivityEvents = [],
  closeAtLastActivityPlusThreshold = false,
  moveThrottleMs = MOVE_ACTIVITY_THROTTLE_MS,
  dualMeasures = false,
}: UseIdleAwareTimeSegmentsOptions): { elapsedSeconds: number; flush: () => Promise<void> } {
  const resolvedIdleThreshold = idleThresholdMs
    ?? (dualMeasures ? PRESENCE_IDLE_THRESHOLD_MS : IDLE_THRESHOLD_MS);
  const resolvedExtras = dualMeasures
    ? Array.from(new Set<keyof WindowEventMap>([
      ...extraActivityEvents,
      ...PRESENCE_EXTRA_ACTIVITY_EVENTS,
    ]))
    : extraActivityEvents;

  const activeStartRef = useRef<number | null>(null);
  const lastStrictActivityRef = useRef(Date.now());
  const lastPresenceActivityRef = useRef(Date.now());
  const activeAccumMsRef = useRef(0);
  const presenceAccumMsRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const sessionKeyRef = useRef<string | null>(sessionKey ?? null);
  const enabledRef = useRef(enabled);
  const onSegmentRef = useRef(onSegment);
  const minSaveRef = useRef(minSaveMinutes);
  const trackElapsedRef = useRef(trackElapsed);
  const idleThresholdRef = useRef(resolvedIdleThreshold);
  const closeAtLastRef = useRef(closeAtLastActivityPlusThreshold);
  const dualRef = useRef(dualMeasures);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMoveBumpRef = useRef(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  onSegmentRef.current = onSegment;
  enabledRef.current = enabled;
  minSaveRef.current = minSaveMinutes;
  trackElapsedRef.current = trackElapsed;
  idleThresholdRef.current = resolvedIdleThreshold;
  closeAtLastRef.current = closeAtLastActivityPlusThreshold;
  dualRef.current = dualMeasures;

  const accumulateToNow = useCallback((now: number) => {
    if (activeStartRef.current == null) {
      lastTickRef.current = now;
      return;
    }
    const prev = lastTickRef.current;
    const delta = Math.max(0, now - prev);
    lastTickRef.current = now;
    if (delta <= 0) return;

    const visible = typeof document === 'undefined' || !document.hidden;
    if (!visible) return;

    const strictActive = now - lastStrictActivityRef.current <= IDLE_THRESHOLD_MS;
    const presenceActive = dualRef.current
      ? (isWindowFocused() && now - lastPresenceActivityRef.current <= PRESENCE_IDLE_THRESHOLD_MS)
      : (now - lastPresenceActivityRef.current <= idleThresholdRef.current);

    if (dualRef.current) {
      if (strictActive) activeAccumMsRef.current += delta;
      if (presenceActive) presenceAccumMsRef.current += delta;
    } else if (presenceActive) {
      activeAccumMsRef.current += delta;
      presenceAccumMsRef.current += delta;
    }
  }, []);

  const resolveEndTime = useCallback((now: number, clearStart: boolean): number => {
    if (!clearStart || !closeAtLastRef.current) return now;
    const last = dualRef.current ? lastPresenceActivityRef.current : lastStrictActivityRef.current;
    const capped = last + idleThresholdRef.current;
    const start = activeStartRef.current ?? now;
    return Math.max(start, Math.min(now, capped));
  }, []);

  const saveSegment = useCallback(async (endTime: number, clearStart: boolean) => {
    if (activeStartRef.current == null) return;
    accumulateToNow(endTime);
    const segmentStart = activeStartRef.current;
    const key = sessionKeyRef.current;
    const resolvedEnd = resolveEndTime(endTime, clearStart);
    const wallMinutes = (resolvedEnd - segmentStart) / 60_000;
    let activeSeconds = Math.round(activeAccumMsRef.current / 1000);
    let presenceSeconds = Math.round(presenceAccumMsRef.current / 1000);
    if (!dualRef.current) {
      // Legacy single measure: minutes ≈ wall clock while active.
      activeSeconds = Math.max(activeSeconds, Math.round(wallMinutes * 60));
      presenceSeconds = activeSeconds;
    }
    const minutes = dualRef.current
      ? Math.max(activeSeconds, 0) / 60
      : wallMinutes;

    if (clearStart) {
      activeStartRef.current = null;
      activeAccumMsRef.current = 0;
      presenceAccumMsRef.current = 0;
      setElapsedSeconds(0);
    } else {
      activeStartRef.current = resolvedEnd;
      activeAccumMsRef.current = 0;
      presenceAccumMsRef.current = 0;
      lastTickRef.current = resolvedEnd;
    }

    if (minutes < minSaveRef.current && presenceSeconds < 1 && activeSeconds < 1) return;
    const reportMinutes = minutes >= minSaveRef.current
      ? minutes
      : Math.max(1 / 60, presenceSeconds / 60, activeSeconds / 60);

    try {
      await onSegmentRef.current({
        start: new Date(segmentStart),
        end: new Date(resolvedEnd),
        minutes: reportMinutes,
        activeSeconds: Math.max(activeSeconds, reportMinutes > 0 ? Math.round(reportMinutes * 60) : 0),
        presenceSeconds: Math.max(presenceSeconds, activeSeconds),
        sessionKey: key,
      });
    } catch (err) {
      console.error('[IdleTime] Failed to save segment:', err);
    }
  }, [accumulateToNow, resolveEndTime]);

  const flushRef = useRef<() => Promise<void>>(async () => undefined);
  flushRef.current = async () => {
    if (!enabledRef.current || activeStartRef.current == null) return;
    const now = Date.now();
    const open = dualRef.current
      ? shouldAccumulateTime(lastPresenceActivityRef.current, now, PRESENCE_IDLE_THRESHOLD_MS)
        && isWindowFocused()
      : shouldAccumulateTime(lastPresenceActivityRef.current, now, idleThresholdRef.current);
    if (!open) return;
    await saveSegment(now, false);
  };

  const syncTrackingRef = useRef<() => void>(() => undefined);
  syncTrackingRef.current = () => {
    if (!enabledRef.current) return;
    const now = Date.now();
    accumulateToNow(now);

    const active = dualRef.current
      ? (!document.hidden
        && isWindowFocused()
        && now - lastPresenceActivityRef.current <= PRESENCE_IDLE_THRESHOLD_MS)
      : shouldAccumulateTime(
        lastPresenceActivityRef.current,
        now,
        idleThresholdRef.current,
      );

    if (!active && activeStartRef.current !== null) {
      void saveSegment(now, true);
      return;
    }

    if (active && activeStartRef.current === null) {
      activeStartRef.current = now;
      lastTickRef.current = now;
      activeAccumMsRef.current = 0;
      presenceAccumMsRef.current = 0;
    }

    if (trackElapsedRef.current) {
      if (active && activeStartRef.current !== null) {
        const secs = dualRef.current
          ? Math.floor(presenceAccumMsRef.current / 1000)
          : Math.floor((now - activeStartRef.current) / 1000);
        setElapsedSeconds(secs);
      } else {
        setElapsedSeconds(0);
      }
    }
  };

  useEffect(() => {
    if (sessionKeyRef.current === (sessionKey ?? null)) return;
    if (activeStartRef.current != null) {
      void saveSegment(Date.now(), true);
    }
    sessionKeyRef.current = sessionKey ?? null;
  }, [sessionKey, saveSegment]);

  useEffect(() => {
    if (!enabled) {
      if (activeStartRef.current != null) void saveSegment(Date.now(), true);
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (tickTimerRef.current) {
        clearInterval(tickTimerRef.current);
        tickTimerRef.current = null;
      }
      activeStartRef.current = null;
      setElapsedSeconds(0);
      return;
    }

    const now = Date.now();
    lastStrictActivityRef.current = now;
    lastPresenceActivityRef.current = now;
    lastTickRef.current = now;
    activeAccumMsRef.current = 0;
    presenceAccumMsRef.current = 0;
    activeStartRef.current = shouldAccumulateTime(now, now, idleThresholdRef.current)
      ? now
      : null;
    setElapsedSeconds(0);

    flushTimerRef.current = setInterval(() => {
      void flushRef.current?.();
    }, flushIntervalMs);

    tickTimerRef.current = setInterval(() => {
      syncTrackingRef.current?.();
    }, tickIntervalMs);

    return () => {
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (tickTimerRef.current) {
        clearInterval(tickTimerRef.current);
        tickTimerRef.current = null;
      }
      void saveSegment(Date.now(), true);
    };
  }, [enabled, flushIntervalMs, tickIntervalMs, saveSegment]);

  useEffect(() => {
    if (!enabled) return;
    const bumpStrict = () => {
      const now = Date.now();
      lastStrictActivityRef.current = now;
      lastPresenceActivityRef.current = now;
      syncTrackingRef.current?.();
    };
    const bumpPresenceOnly = () => {
      const now = Date.now();
      if (moveThrottleMs > 0 && now - lastMoveBumpRef.current < moveThrottleMs) return;
      lastMoveBumpRef.current = now;
      lastPresenceActivityRef.current = now;
      syncTrackingRef.current?.();
    };
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, bumpStrict, opts);
    }
    for (const ev of resolvedExtras) {
      if ((ACTIVITY_EVENTS as string[]).includes(ev)) continue;
      const handler = ev === 'mousemove' || ev === 'pointermove' ? bumpPresenceOnly : bumpStrict;
      window.addEventListener(ev, handler, opts);
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, bumpStrict, opts);
      }
      for (const ev of resolvedExtras) {
        if ((ACTIVITY_EVENTS as string[]).includes(ev)) continue;
        const handler = ev === 'mousemove' || ev === 'pointermove' ? bumpPresenceOnly : bumpStrict;
        window.removeEventListener(ev, handler, opts);
      }
    };
  }, [enabled, resolvedExtras, moveThrottleMs]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        lastStrictActivityRef.current = now;
        lastPresenceActivityRef.current = now;
        lastTickRef.current = now;
      }
      syncTrackingRef.current?.();
    };
    const onPageHide = () => {
      void saveSegment(Date.now(), true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [saveSegment]);

  const flush = useCallback(async () => {
    await flushRef.current?.();
  }, []);

  return { elapsedSeconds, flush };
}
